/**
 * THÔNG BÁO — the real server inbox.
 *
 * Two layers, in the order they matter on a shift:
 *
 *   1. CẦN XỬ LÝ NGAY — live dispatch offers (with their real countdown) and
 *      urgent tasks, derived from the task/home queries. These are not
 *      notifications; they are the things whose deadline is running while the
 *      employee reads this screen.
 *   2. HỘP THÔNG BÁO — `GET /staff/notifications`, one row per delivery
 *      addressed to this user. Read state is SERVER state
 *      (`POST /staff/notifications/:id/read`), so the badge agrees across
 *      every device; "đọc tất cả" uses the generic `/notifications/read-all`.
 *
 * DEEP LINKS: the backend writes domain paths (`/tasks/<id>`, `/orders/<id>`)
 * shared by all three PWAs. `staffRouteFor` maps the ones this app can render
 * onto its own routes; a link with no staff-side surface simply renders without
 * a navigation button rather than dead-ending on a 404 route.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { errorMessage, queryKeys, useHome, useNotifications, useTasks } from '../lib/queries';
import { useServerClock } from '../lib/hooks';
import { formatCountdown, formatTimeDate } from '../lib/format';
import { taskTypeLabel } from '../lib/labels';
import type { StaffNotification, StaffNotificationsResponse } from '../lib/contracts';
import { Button, Card, EmptyState, ErrorState, LoadingState, SectionHeader, StatusBadge } from '../components/ui';
import { useToast } from '../components/Toast';
import type { Tone } from '../lib/labels';

// ---------------------------------------------------------------------------
// Category / priority presentation
// ---------------------------------------------------------------------------

const CATEGORY_LABEL: Readonly<Record<string, string>> = {
  TASK: 'Công việc',
  ORDER: 'Đơn hàng',
  KITCHEN: 'Bếp',
  ALERT: 'Cảnh báo',
  HR: 'Nhân sự',
  PAYROLL: 'Lương',
  SYSTEM: 'Hệ thống',
  CUSTOMER: 'Khách hàng',
  TRANSLATION: 'Bản dịch',
  APPROVAL: 'Phê duyệt',
};

function categoryLabel(category: string): string {
  return CATEGORY_LABEL[category] ?? category;
}

function priorityTone(priority: string): Tone {
  if (priority === 'URGENT') return 'danger';
  if (priority === 'HIGH') return 'warning';
  return 'info';
}

/** Map a backend deep link onto a route THIS app actually has. */
export function staffRouteFor(notification: StaffNotification): string | null {
  const link = notification.deepLink;
  if (link !== null) {
    const task = /^\/tasks\/([^/]+)$/.exec(link);
    if (task !== null) return `/cong-viec/${task[1] ?? ''}`;
    // A link that is already one of this app's own routes passes through.
    if (/^\/(cong-viec|ca-nhan|cham-cong|thong-bao|quet-qr)(\/|$)/.test(link)) return link;
  }
  if (notification.resourceType === 'task' && notification.resourceId !== null) {
    return `/cong-viec/${notification.resourceId}`;
  }
  switch (notification.category) {
    case 'TASK':
      return '/cong-viec';
    case 'PAYROLL':
      return '/ca-nhan/luong-tam-tinh';
    case 'HR':
      return '/ca-nhan/lich-ca';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function NotificationsScreen(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const home = useHome();
  const tasks = useTasks();
  const inbox = useNotifications();
  const now = useServerClock(home.data?.serverTime, 1000);

  const unreadCount = (inbox.data?.notifications ?? []).filter((n) => n.readAt === null).length;

  /**
   * Server read-state, updated optimistically: the row and the tab badge both
   * read from this cache, so a tap greys the row immediately and a failed POST
   * simply gets refetched back to unread — visible, not silent.
   */
  const markRead = useMutation({
    mutationFn: (deliveryId: string) => api.markNotificationRead(deliveryId),
    onMutate: async (deliveryId: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications });
      queryClient.setQueryData<StaffNotificationsResponse>(queryKeys.notifications, (previous) =>
        previous === undefined
          ? previous
          : {
              ...previous,
              notifications: previous.notifications.map((n) =>
                n.deliveryId === deliveryId && n.readAt === null ? { ...n, readAt: Date.now() } : n,
              ),
            },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
    },
  });

  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: (result) => {
      toast.success('Đã đánh dấu đã đọc', `${result.marked} thông báo.`);
    },
    onError: (error) => toast.error('Không đánh dấu được', errorMessage(error)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      void queryClient.invalidateQueries({ queryKey: queryKeys.home });
    },
  });

  const open = (notification: StaffNotification): void => {
    if (notification.readAt === null) markRead.mutate(notification.deliveryId);
    const route = staffRouteFor(notification);
    if (route !== null) navigate(route);
  };

  // Live offers whose countdown is still running.
  const liveOffers = useMemo(
    () => (tasks.data?.offered ?? []).filter((offer) => offer.offerExpiresAt > now),
    [tasks.data, now],
  );
  const urgent = home.data?.urgentTasks ?? [];

  return (
    <div className="space-y-3 pb-8">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">Thông báo</h1>
        {unreadCount > 0 ? (
          <Button variant="ghost" onClick={() => markAll.mutate()} loading={markAll.isPending}>
            Đọc tất cả ({unreadCount})
          </Button>
        ) : null}
      </div>

      {/* --- layer 1: deadlines running right now ------------------------- */}
      {liveOffers.length === 0 && urgent.length === 0 ? null : (
        <>
          <SectionHeader title="Cần xử lý ngay" />
          {liveOffers.map((offer) => {
            const secondsLeft = Math.max(0, Math.floor((offer.offerExpiresAt - now) / 1000));
            return (
              <Card key={`offer:${offer.offerId}`} className="ring-2 ring-brand-300">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <StatusBadge tone={secondsLeft <= 30 ? 'danger' : 'info'}>Điều phối việc</StatusBadge>
                    <p className="mt-2 font-bold text-slate-900">Mời bạn nhận: {offer.title}</p>
                    <p className="mt-0.5 text-sm text-slate-600">
                      {taskTypeLabel(offer.type)} · còn{' '}
                      <span className="font-bold tabular-nums">{formatCountdown(secondsLeft)}</span> để phản hồi
                    </p>
                  </div>
                </div>
                <Button size="lg" fullWidth className="mt-3" onClick={() => navigate('/cong-viec')}>
                  Xem và nhận
                </Button>
              </Card>
            );
          })}
          {urgent
            .filter((task) => !liveOffers.some((offer) => offer.id === task.taskId))
            .map((task) => (
              <Card key={`urgent:${task.taskId}`}>
                <StatusBadge tone="danger">Việc gấp</StatusBadge>
                <p className="mt-2 font-bold text-slate-900">{task.title}</p>
                <p className="mt-0.5 text-sm text-slate-600">{taskTypeLabel(task.type)} · cần xử lý ngay</p>
                <Button size="lg" fullWidth className="mt-3" onClick={() => navigate('/cong-viec')}>
                  Mở công việc
                </Button>
              </Card>
            ))}
        </>
      )}

      {/* --- layer 2: the server inbox ------------------------------------ */}
      <SectionHeader title="Hộp thông báo" />
      {inbox.isLoading ? (
        <LoadingState label="Đang tải thông báo…" />
      ) : inbox.isError ? (
        <ErrorState message={errorMessage(inbox.error)} onRetry={() => void inbox.refetch()} />
      ) : (inbox.data?.notifications.length ?? 0) === 0 ? (
        <EmptyState
          title="Chưa có thông báo nào"
          description="Thông báo điều phối việc, duyệt đơn từ, thay đổi lịch ca và cảnh báo sẽ hiện ở đây."
        />
      ) : (
        (inbox.data?.notifications ?? []).map((notification) => {
          const isRead = notification.readAt !== null;
          const route = staffRouteFor(notification);
          return (
            <Card key={notification.deliveryId} className={isRead ? 'opacity-70' : ''}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={priorityTone(notification.priority)}>
                      {categoryLabel(notification.category)}
                    </StatusBadge>
                    {isRead ? null : (
                      <span aria-label="Chưa đọc" className="h-2.5 w-2.5 rounded-full bg-rose-600" />
                    )}
                  </div>
                  <p className="mt-2 font-bold text-slate-900">{notification.title}</p>
                  {notification.body === null ? null : (
                    <p className="mt-0.5 text-sm text-slate-600">{notification.body}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-400">{formatTimeDate(notification.createdAt)}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {isRead ? (
                  <span />
                ) : (
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => markRead.mutate(notification.deliveryId)}
                  >
                    Đã đọc
                  </Button>
                )}
                {route === null ? (
                  <span />
                ) : (
                  <Button size="lg" onClick={() => open(notification)}>
                    Mở
                  </Button>
                )}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
